import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@hooks/permissions';
import { getDefaultLandingPath, Permission } from '@consts/permissions';
import { LoginStatus, useAuthStore } from '@stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  fallbackPath?: string;
}

const resolveFallbackPath = (fallbackPath: string, userPermissions: Permission[]) => {
  return fallbackPath === '/dashboard' ? getDefaultLandingPath(userPermissions) : fallbackPath;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  fallbackPath = '/dashboard',
}) => {
  const { canAccess, isAdmin, userPermissions } = usePermissions();
  const authStore = useAuthStore();
  const location = useLocation();

  // 인증 로딩 중일 때는 아무것도 렌더링하지 않음 (useAuthentication이 처리)
  if (authStore.status !== LoginStatus.LOGIN) {
    return null;
  }

  // 관리자 권한이 있으면 모든 페이지 접근 가능
  if (isAdmin()) {
    return <>{children}</>;
  }

  // 권한이 필요한 경우 권한 확인
  if (requiredPermissions.length > 0 && !canAccess(requiredPermissions)) {
    const landingPath = resolveFallbackPath(fallbackPath, userPermissions);
    if (landingPath === '/login' || location.pathname === landingPath) {
      return <div style={{ padding: 24 }}>이 페이지에 접근할 수 없습니다.</div>;
    }
    return <Navigate to={landingPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// 페이지별 권한을 자동으로 확인하는 컴포넌트
interface PageProtectedRouteProps {
  children: React.ReactNode;
  path: string;
  fallbackPath?: string;
}

export const PageProtectedRoute: React.FC<PageProtectedRouteProps> = ({
  children,
  path,
  fallbackPath = '/dashboard',
}) => {
  const { canAccessPath, isAdmin, userPermissions } = usePermissions();
  const authStore = useAuthStore();
  const location = useLocation();

  // 인증 로딩 중일 때는 아무것도 렌더링하지 않음 (useAuthentication이 처리)
  if (authStore.status !== LoginStatus.LOGIN) {
    return null;
  }

  // 관리자 권한이 있으면 모든 페이지 접근 가능
  if (isAdmin()) {
    return <>{children}</>;
  }

  // 페이지 접근 권한 확인
  if (!canAccessPath(path)) {
    const landingPath = resolveFallbackPath(fallbackPath, userPermissions);
    if (landingPath === '/login' || location.pathname === landingPath) {
      return <div style={{ padding: 24 }}>이 페이지에 접근할 수 없습니다.</div>;
    }
    return <Navigate to={landingPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
